import React, { Component } from "react";
import { observer } from "mobx-react";
import { AxiosResponse, AxiosError } from "axios";
import { readString } from "react-papaparse";
import { putRequest, postRequest } from "../../../utils/httpRequest";
import {
  formErrors,
  notification,
  randomNumber,
  Errors
} from "../../../utils/helpers";
import Uppy from "@uppy/core";
import Transloadit from "@uppy/transloadit";
import { TeamResource } from "../../../stores/TeamStore/Types";
import TeamStore from "../../../stores/TeamStore";

interface Props {
  showSettingsModal: boolean;
  handleShowSettingsModal: (val: boolean) => void;
  teamResource: TeamResource;
  teamStore: TeamStore;
  getHeroes: () => void;
}

interface ImportedUser {
  name: string;
  email: string;
}

interface State {
  name: string;
  logoUrl: string;
  [x: string]: any;
  addMode: "paste" | "csv";
  emails: string;
  importedUsers: ImportedUser[];
  csvFileName: string;
  csvError: string;
  dragging: boolean;
  savingTeam: boolean;
  teamErrors: any;
  uploadNewImage: boolean;
  progress: number;
  uploadingImage: boolean;
}

const monogram = (name: string) =>
  (name || "")
    .replace(/[^\w\s]/g, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

class Settings extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      name: "",
      logoUrl: "",
      addMode: "paste",
      emails: "",
      importedUsers: [],
      csvFileName: "",
      csvError: "",
      dragging: false,
      savingTeam: false,
      teamErrors: {},
      uploadNewImage: false,
      progress: 0,
      uploadingImage: false
    };

    (this as any).uppy = Uppy({
      autoProceed: true
    });

    (this as any).uppy
      .use(Transloadit, {
        params: {
          auth: {
            key: process.env.TRANSLOADIT_AUTH_KEY,
            secret: process.env.TRANSLOADIT_AUTH_SECRET,
            duration: 1800
          },
          templates: {
            s3_store: process.env.TRANSLOADIT_FILE_TEMPLATE_ID
          },
          template_id: process.env.TRANSLOADIT_PROFILE_PIC_TEMPLATE_ID,

          // It’s more secure to use a template_id and enable
          // Signature Authentication
          steps: {
            resize: {
              robot: "/image/resize",
              width: 250,
              height: 250,
              resize_strategy: "fit",
              format: "webp"
            }
          }
        },
        waitForEncoding: true
      })
      .on("error", error => {
        this.setState({ uploadingImage: false });
        notification("Error uploading image. Please try again", "error");
        console.error(error);
        if (error.assembly) {
          console.error(`Assembly ID ${error.assembly.assembly_id} failed!`);
          console.error(error.assembly);
        }
        (window as any).honeybadger?.notify(`Error uploading logo ${error}`);
      })
      .on("upload-error", (file, error, response) => {
        console.error("error with file:", file.id);
        console.error("error message:", error);
        console.error("error response:", response);
        this.setState({ uploadingImage: false });
        notification("Error uploading image. Please try again", "error");
        (window as any).honeybadger?.notify(`Error uploading logo ${error}`);
      })
      .on("transloadit:result", (stepName, result) => {
        this.uploadFinished(result.url);
      });
  }

  uploadFinished = url => {
    this.setState({ logoUrl: url, progress: 100 });
    setTimeout(() => {
      this.setState({ uploadingImage: false });
    }, 500);
  };

  componentDidMount() {
    this.initialState();
  }

  initialState = () => {
    const { teamResource } = this.props;

    this.setState({
      name: teamResource.name,
      logoUrl: teamResource.logo_url,
      addMode: "paste",
      emails: "",
      importedUsers: [],
      csvFileName: "",
      csvError: "",
      dragging: false,
      teamErrors: {},
      uploadNewImage: false
    });
  };

  // The dialog stays mounted, so anything half-entered has to be cleared by
  // hand -- otherwise it is still sitting there the next time it opens.
  closeModal = () => {
    this.props.handleShowSettingsModal(false);
    this.initialState();
  };

  //Add your search logic here.
  customFilter(option, searchText) {
    if (
      option.data.label.toLowerCase().includes(searchText.toLowerCase()) ||
      option.data.value.toLowerCase().includes(searchText.toLowerCase())
    ) {
      return true;
    } else {
      return false;
    }
  }

  handleChange(key, e) {
    this.setState({ [key]: e.target.value });
  }

  selectAddMode = (addMode: "paste" | "csv") => this.setState({ addMode });

  fileExtension = (fileName: string) =>
    fileName
      ?.split(".")
      ?.pop()
      ?.toLowerCase();

  isCSVFile = (fileName: string): boolean =>
    this.fileExtension(fileName) === "csv";

  // Same shape the old allocate-users modal accepted: a header row, then name
  // in the first column and email in the second.
  readCSV = (file: File) => {
    if (!file) return;

    if (!this.isCSVFile(file.name)) {
      this.discardCSV();
      this.setState({
        csvError:
          "Looks like this is not a valid CSV file. Please make sure to save your file as a CSV."
      });
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const { data } = readString<string[]>(String(reader.result), {
        header: false,
        skipEmptyLines: true
      });

      const importedUsers = data
        .slice(1)
        .map(row => ({
          name: (row[0] || "").trim(),
          email: (row[1] || "").trim()
        }))
        .filter(user => user.email);

      this.setState({
        importedUsers,
        csvFileName: file.name,
        csvError: importedUsers.length
          ? ""
          : "We couldn’t find any learners in that file. It needs a name column, then an email column, under a header row."
      });
    };

    reader.onerror = () => {
      this.discardCSV();
      this.setState({ csvError: "We couldn’t read that file. Please try again." });
    };

    reader.readAsText(file);
  };

  discardCSV = () =>
    this.setState({ importedUsers: [], csvFileName: "", csvError: "" });

  handleCSVInput = e => {
    this.readCSV(e.target.files && e.target.files[0]);
    e.target.value = null;
  };

  handleDragOver = e => {
    e.preventDefault();
    this.setState({ dragging: true });
  };

  handleDragLeave = () => this.setState({ dragging: false });

  handleDrop = e => {
    e.preventDefault();
    this.setState({ dragging: false });
    this.readCSV(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  updateTeam = () => {
    const { teamResource } = this.props;
    const { name, logoUrl } = this.state;

    const data = {
      organization: {
        name,
        logo_url: logoUrl
      }
    };

    return putRequest(`/api/teams/${teamResource.slug}.json`, data)
      .then((response: AxiosResponse) => {
        this.props.teamStore.setTeamResource(response.data);
        this.setState({ teamErrors: {} });
      })
      .catch((error: AxiosError<{ errors: Errors }>) => {
        const teamErrors = formErrors(error.response.data.errors);
        this.setState({ teamErrors });
        throw error;
      });
  };

  // Resolves to whether anything was actually imported, so the confirmation
  // can say so. Both fields go up together: the API takes either.
  importMembers = (): Promise<boolean> => {
    const { teamResource, getHeroes } = this.props;
    const { emails, importedUsers } = this.state;

    if (!emails.trim() && importedUsers.length === 0) {
      return Promise.resolve(false);
    }

    const data = {
      import_members: {
        emails,
        users: importedUsers
      }
    };

    return postRequest(
      `/api/teams/${teamResource.slug}/import_members.json`,
      data
    )
      .then(() => {
        getHeroes();
        return true;
      })
      .catch((error: AxiosError<{ error: string }>) => {
        notification(error.response.data.error, "error");
        throw error;
      });
  };

  save = e => {
    e.preventDefault();
    this.setState({ savingTeam: true });

    return this.updateTeam()
      .then(this.importMembers)
      .then(imported => {
        this.setState({ savingTeam: false });
        this.closeModal();
        notification(
          imported
            ? "Team updated, and your learners are on their way in."
            : "Team was successfully updated.",
          "notification"
        );
      })
      // Both steps report their own failure and leave the dialog open so the
      // work isn't lost.
      .catch(() => this.setState({ savingTeam: false }));
  };

  disableUploadingImage = () => {
    this.setState({ uploadingImage: false });
  };

  addFile(file) {
    const that = this;

    const descriptor = {
      id: "FileInput",
      name: randomNumber() + file.name,
      type: file.type,
      data: file
    };

    try {
      (this as any).uppy.addFile(descriptor);
    } catch (err) {
      this.disableUploadingImage();
      console.log(err);

      (this as any).uppy.log(err);

      notification("Error uploading image. Please try again", "error");
      return;
    }
  }

  toArray(list) {
    return Array.prototype.slice.call(list || [], 0);
  }

  handleUploadNewImage = e => {
    e.preventDefault();

    this.setState({ uploadNewImage: true });
  };

  uploadFile = e => {
    (this as any).uppy.log("[FileInput] Something selected through input...");
    const file = e.target.files[0];
    this.setState({ uploadingImage: true, progress: 50 });
    this.addFile(file);

    e.target.value = null;
  };

  render() {
    const { showSettingsModal, teamResource } = this.props;

    const {
      name,
      logoUrl,
      addMode,
      emails,
      importedUsers,
      csvFileName,
      csvError,
      dragging,
      savingTeam,
      progress,
      uploadingImage,
      teamErrors
    } = this.state;

    const getErrorMessageFor = type => {
      return teamErrors[type];
    };

    const nameError = getErrorMessageFor("name");

    return (
      <div className="team-manage-modal">
        <div
          className={`backdrop ${showSettingsModal ? "is-open" : ""}`}
          aria-hidden={!showSettingsModal}
          onClick={(event) => {
            if (event.target === event.currentTarget) this.closeModal();
          }}
        >
          <div
            className="dialog dialog-lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="settingsTitle"
          >
            <button
              className="dialog-close"
              type="button"
              aria-label="Close"
              onClick={this.closeModal}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>

            {/* Only this middle region scrolls, so the footer buttons are never
                pushed off the bottom on a short viewport. */}
            <div className="composer-scroll">
              <h2 id="settingsTitle">Team settings</h2>
              <p className="dialog-intro">
                Update how your team appears, and add new learners.
              </p>

              <div className="set-section">
                <h3>Team identity</h3>
                <div className="set-identity">
                  <div className="logo-edit">
                    <span
                      className="set-logo"
                      data-monogram={monogram(teamResource.name)}
                    >
                      {logoUrl && (
                        <img
                          src={logoUrl}
                          alt={`${teamResource.name} logo`}
                          onError={(event) => {
                            (event.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      )}
                    </span>
                    <label
                      className="logo-edit-btn"
                      htmlFor="team-logo-upload"
                      aria-label="Change team logo"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 20h9M3 17l-1 4 4-1L17.5 7.5a2.1 2.1 0 0 0-3-3L3 17Z" />
                      </svg>
                    </label>
                    <input
                      id="team-logo-upload"
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={this.uploadFile}
                    />
                    {uploadingImage && (
                      <div className="logo-progress" role="status">
                        <span style={{ width: `${progress}%` }} />
                      </div>
                    )}
                  </div>

                  <div className="id-fields">
                    <label className="nf-label" htmlFor="setTeamName">
                      Team name
                    </label>
                    <input
                      className="nf-input"
                      type="text"
                      id="setTeamName"
                      value={name}
                      onChange={this.handleChange.bind(this, "name")}
                    />
                    {nameError && <div className="nf-error">{nameError}</div>}
                    <p className="nf-hint">
                      Member since <b>{teamResource.formatted_created_at}</b>
                    </p>
                  </div>
                </div>
              </div>

              <div className="set-section">
                <h3>Add learners</h3>
                <div className="seg" role="tablist" aria-label="How to add learners">
                  <button
                    type="button"
                    role="tab"
                    id="tabPaste"
                    aria-selected={addMode === "paste"}
                    aria-controls="panePaste"
                    onClick={() => this.selectAddMode("paste")}
                  >
                    Paste a list
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="tabCsv"
                    aria-selected={addMode === "csv"}
                    aria-controls="paneCsv"
                    onClick={() => this.selectAddMode("csv")}
                  >
                    Upload CSV
                  </button>
                </div>

                {/* Both panes stay mounted, so switching tabs to check the CSV
                    format doesn't throw away a half-pasted list. */}
                <div
                  id="panePaste"
                  role="tabpanel"
                  aria-labelledby="tabPaste"
                  hidden={addMode !== "paste"}
                >
                  <p className="add-hint">
                    Add user emails, comma-separated. We will invite each of
                    them to join the team.
                  </p>
                  <textarea
                    className="nf-textarea"
                    aria-label="User emails"
                    placeholder="jane@company.com, sam@company.com, ..."
                    value={emails}
                    onChange={this.handleChange.bind(this, "emails")}
                  />
                </div>

                <div
                  id="paneCsv"
                  role="tabpanel"
                  aria-labelledby="tabCsv"
                  hidden={addMode !== "csv"}
                >
                  <p className="add-hint">
                    Drag in a CSV with two columns: <b>name</b> then{" "}
                    <b>email</b>. As long as it follows that shape, every
                    learner is added and invited automatically.
                  </p>
                  {/* The file input is nested and only visually hidden, so the
                      dropzone is still reachable by keyboard -- a `hidden`
                      input takes no focus and would strand it. */}
                  <label
                    className={`dropzone ${dragging ? "drag" : ""}`}
                    onDragEnter={this.handleDragOver}
                    onDragOver={this.handleDragOver}
                    onDragLeave={this.handleDragLeave}
                    onDrop={this.handleDrop}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 16V4M7 9l5-5 5 5M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
                    </svg>
                    <div className="dz-main">
                      <b>Choose a file</b> or drag it here
                    </div>
                    <div className="dz-sub">CSV up to 5MB</div>
                    <input
                      className="sr-only"
                      type="file"
                      accept=".csv,text/csv"
                      onChange={this.handleCSVInput}
                    />
                  </label>

                  {csvError && <div className="nf-error">{csvError}</div>}

                  {importedUsers.length > 0 && (
                    <div className="set-filechip">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="m5 13 4 4L19 7" />
                      </svg>
                      <span className="chip-name">{csvFileName}</span>
                      <span>
                        &mdash; {importedUsers.length}{" "}
                        {importedUsers.length === 1 ? "learner" : "learners"}{" "}
                        ready to import
                      </span>
                      <button
                        className="chip-discard"
                        type="button"
                        onClick={this.discardCSV}
                      >
                        Remove
                      </button>
                    </div>
                  )}

                  <div className="csv-spec">
                    <table>
                      <caption className="sr-only">Required CSV format</caption>
                      <thead>
                        <tr>
                          <th>Column 1</th>
                          <th>Column 2</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="colhdr">name</td>
                          <td className="colhdr">email</td>
                        </tr>
                        <tr>
                          <td>Jane Doe</td>
                          <td>jane@company.com</td>
                        </tr>
                        <tr>
                          <td>Sam Okafor</td>
                          <td>sam@company.com</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="composer-foot">
              <button
                className="btn btn-ghost btn--sm"
                type="button"
                onClick={this.closeModal}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn--sm"
                type="button"
                disabled={savingTeam}
                onClick={this.save}
              >
                {savingTeam ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default observer(Settings);
